<script>
import {
  agentDetailsComputed,
  agentDetailsMethods,
} from '@state/helpers/agent-details'
import pullAt from 'lodash/pullAt'
import join from 'lodash/join'
import { STATUS_REQUEST, ROLE } from '@utils/constant'

export default {
  props: {
    order: {
      type: Number,
      require: true,
      default: 1,
    },
    triggerMask: {
      type: Boolean,
      require: false,
    },
  },
  data() {
    return {
      items: {},
      editable: false,
      show: false,
      answers: [],
      ROLE: ROLE,
      rulesQuestion: { required: false, scope: { min: 0, max: 30 } },
      rulesAnswer: {},
      rows: 1,
    }
  },
  computed: {
    ...agentDetailsComputed, // state and getters vuex
    itemsComputed() {
      if (this.getDetails) {
        return this.getDataByOrder()
      }
      return this.items
    },
    currentUserLevel() {
      const user = this.$store.getters['auth/getCurrentUser']
      return user?.level
    },
    showComputed() {
      if (this.getDetails) {
        return !!this.getDetails[`question${this.order}`]
      }
      return false
    },
  },
  watch: {
    getDetails() {
      this.initial()
    },
    items() {
      if (
        this.items[`radio_answer${this.order}`] === '1' &&
        !this.answers.length
      ) {
        this.answers.push('')
      }
    },
    getEditStatus() {
      // if edit success toggle editable to readonly
      if (this.getEditStatus === STATUS_REQUEST.SUCCESS) {
        this.editable = false
      }
    },
  },
  methods: {
    ...agentDetailsMethods, // add action vuex
    initial() {
      this.items = this.getDataByOrder()
      if (this.items && this.items[`answer${this.order}`]) {
        this.answers = this.items[`answer${this.order}`].split(',')
      } else {
        this.answers = []
      }
    },
    toggleEditable() {
      this.initial()
      this.editable = !this.editable
      this.$emit('enable:edit', this.editable)
    },
    getDataByOrder() {
      const details = this.getDetails
      const data = {}
      data[`question${this.order}`] = details[`question${this.order}`]
      data[`answer${this.order}`] = details[`answer${this.order}`]
      data[`question${this.order}_required`] = `${
        details[`question${this.order}_required`]
      }`
      if (details[`answer${this.order}`] || !details[`question${this.order}`]) {
        data[`radio_answer${this.order}`] = '1'
      } else {
        data[`radio_answer${this.order}`] = '0'
      }
      data[`answer${this.order}`] = details[`answer${this.order}`]
      return data
    },
    handleAddMoreAnswers() {
      this.answers.push('')
    },
    handleUpdate(field, value) {
      this.items = { ...this.items, [field]: value }
    },
    handleRemove(index) {
      const cloneAnswers = [...this.answers]
      pullAt(cloneAnswers, [index])
      this.answers = cloneAnswers
    },

    updateQuestion() {
      let answer = join(this.answers, ',')
      if (this.items[`radio_answer${this.order}`] === '0') {
        answer = ''
      }

      if (!this.items[`question${this.order}`]) {
        answer = ''
      }
      const payload = {
        ...this.items,
        [`answer${this.order}`]: answer,
      }
      payload[`question${this.order}_required`] = parseInt(
        payload[`question${this.order}_required`],
        10
      )
      this.updateAgent({ ...this.getDetails, ...payload })
    },
  },
}
</script>

<template>
  <div id="agent-details-question">
    <BaseCardDetail
      :title="`質問${order}`"
      :form-collapsed="showComputed || editable"
      :has-footer="editable"
      :trigger-mask="!editable && triggerMask"
      @edit="toggleEditable"
      @cancel="toggleEditable"
      @submit="updateQuestion"
    >
      <template v-slot:header-action="{ edit }">
        <CButton
          v-show="
            currentUserLevel === ROLE.SUPER ||
              currentUserLevel === ROLE.AGENT_ADMIN ||
              currentUserLevel === ROLE.AGENT_SUB
          "
          variant="ghost"
          @click="edit"
        >
          <CIcon name="cil-pencil" />
        </CButton>
      </template>
      <CRow class="align-items-baseline">
        <CCol sm="3">
          <label>
            質問
          </label>
        </CCol>
        <CCol
          v-if="
            currentUserLevel === ROLE.SUPER ||
              currentUserLevel === ROLE.AGENT_ADMIN ||
              currentUserLevel === ROLE.AGENT_SUB
          "
          sm="9"
        >
          <BaseInput
            :value="items[`question${order}`]"
            :name="`question${order}`"
            :plaintext="!editable"
            :rules="rulesQuestion"
            @update:value="(v) => handleUpdate(`question${order}`, v)"
          />
        </CCol>
        <CCol v-else sm="9">
          <BaseInput
            :value="items[`question${order}`]"
            :name="`question${order}`"
            plaintext
            @update:value="(v) => handleUpdate(`question${order}`, v)"
          />
        </CCol>
      </CRow>
      <CRow class="align-items-baseline">
        <CCol sm="3">
          <label>
            必須/任意
          </label>
        </CCol>
        <CCol sm="9">
          <BaseRadioGroup
            inline
            custom
            :checked.sync="items[`question${order}_required`]"
            :options="[
              { value: '1', label: '必須' },
              { value: '0', label: '任意' },
            ]"
            :name="`question${order}_required`"
            :disabled="!editable"
            @update:checked="
              (v) => handleUpdate(`question${order}_required`, v)
            "
          />
        </CCol>
      </CRow>
      <CRow class="align-items-baseline">
        <CCol sm="3">
          <label>
            回答形式
          </label>
        </CCol>
        <CCol sm="9">
          <BaseRadioGroup
            inline
            custom
            :checked.sync="items[`radio_answer${order}`]"
            :options="[
              { value: '1', label: '選択' },
              { value: '0', label: '自由入力' },
            ]"
            :name="`radio_answer${order}`"
            :disabled="!editable"
            @update:checked="(v) => handleUpdate(`radio_answer${order}`, v)"
          />
        </CCol>
      </CRow>
      <CRow
        v-if="items[`radio_answer${order}`] === '1'"
        class="align-items-baseline"
      >
        <CCol sm="3">
          <label>
            回答リスト
            <span style="font-size: xx-small">（最大50項目）</span>
          </label>
        </CCol>
        <CCol sm="9">
          <div
            v-for="(answer, index) in answers"
            :key="index"
            :class="{
              'd-flex align-items-baseline': true,
              'flex-wrap': !index,
              'anwser-item': true,
            }"
          >
            <BaseInput
              v-show="editable"
              v-model="answers[index]"
              :plaintext="!editable"
              :rules="{
                required: items[`question${order}`] !== '',
                max: 100,
              }"
              class="flex-grow-1"
              inline
            />
            <BaseTextarea
              v-show="!editable"
              v-model="answers[index]"
              :plaintext="!editable"
              :rules="rulesAnswer"
              class="flex-grow-1"
              :rows="rows"
              inline
            />
            <CButton
              v-if="index && editable"
              color="danger"
              size="sm"
              class="ml-2"
              variant="ghost"
              @click="handleRemove(index)"
            >
              <CIcon name="cil-x-circle" color="danger" />
            </CButton>
          </div>
        </CCol>
      </CRow>
      <CRow
        v-if="
          editable && answers.length && items[`radio_answer${order}`] === '1'
        "
      >
        <CCol sm="3"> </CCol>
        <CCol sm="9">
          <label v-if="editable" style="font-size: xx-small;" class="ml-0"
            >各項目100文字以内で入力してください
          </label>
        </CCol>
        <CCol sm="3"> </CCol>
        <CCol sm="9">
          <CButton
            :disabled="answers.length > 49"
            color="primary"
            variant="ghost"
            class="add-answer-btn"
            @click="handleAddMoreAnswers"
          >
            <font-awesome-icon icon="plus-circle" size="lg" />
          </CButton>
        </CCol>
      </CRow>
    </BaseCardDetail>
  </div>
</template>

<style lang="scss" scoped>
#agent-details-question {
  ::v-deep .add-answer-btn {
    margin-left: -0.75rem;
  }
  ::v-deep .anwser-item {
    position: relative;

    &::before {
      position: absolute;
      top: 16px;
      left: -15px;
      width: 5px;
      height: 5px;
      content: '';
      background-color: #000;
      border-radius: 50%;
    }
  }
  ::v-deep textarea {
    min-height: 35px;
  }
}
</style>
